import { getBPId } from '@telialt/utils/src/SSO/utils';

import { createGTMEcommerceEvent, createGTMEvent } from '../../core/ecommerceMapper';
import { DEFAULT_CURRENCY, DEFAULT_QUANTITY } from '../../core/normalizers';
import type { GTMEcommerceEvent, GTMEventName, TrackingCommerce, TrackingItem } from '../../types';
import type { SearchEventInput, SearchProductClickInput, SearchProductsEventData, SearchProductSource } from './types';

const SEARCH_EVENTS = {
    AutocompleteProductClick: 'autocomplete_product_click',
    AutocompleteSuggestionClick: 'autocomplete_suggestion_click',
    ProductRecommendationClick: 'product_recommendation_click',
    SearchFormSubmit: 'search_form_submit',
    SearchProductClick: 'search_product_click',
    SearchQuery: 'search_query',
    SearchResultClick: 'search_result_click',
    SearchZeroResults: 'search_zero_results',
} as const satisfies Record<string, GTMEventName>;

const mapProductVariant = (product: SearchProductSource) =>
    [product.color, product.internalMemory].filter(Boolean).join(' ');

const getProductId = (product: SearchProductSource) => product.variantCode || product.code;

const mapSearchProductToTrackingItem = (product: SearchProductSource, itemListName: string): TrackingItem => ({
    id: getProductId(product),
    index: 0,
    name: product.name,
    brand: product.brand,
    category: product.category || itemListName,
    listName: itemListName,
    variant: mapProductVariant(product),
    currency: DEFAULT_CURRENCY,
    price: product.fullPrice,
    quantity: DEFAULT_QUANTITY,
});

export const mapSearchProductsToTrackingCommerce = ({
    products,
    searchText,
    itemListName,
    itemCategory,
}: SearchProductsEventData): TrackingCommerce => ({
    items: products.map((product) => mapSearchProductToTrackingItem(product, itemListName)),
    itemListName,
    searchText,
    itemCategory,
});

const createSearchTextEvent = (event: GTMEventName, searchText: string): GTMEcommerceEvent =>
    createGTMEcommerceEvent(event, { searchText });

export const createSearchGAEvent = (input: SearchEventInput): GTMEcommerceEvent => {
    switch (input.source) {
        case 'query':
            return createGTMEcommerceEvent(SEARCH_EVENTS.SearchQuery, {
                items: input.data.products?.map((product) =>
                    mapSearchProductToTrackingItem(product, 'search-autocomplete'),
                ),
                searchText: input.data.searchText,
            });
        case 'formSubmit':
            return createSearchTextEvent(SEARCH_EVENTS.SearchFormSubmit, input.data.searchText);
        case 'zeroResults':
            return createSearchTextEvent(SEARCH_EVENTS.SearchZeroResults, input.data.searchText);
        case 'autocompleteSuggestionClick':
            return createGTMEcommerceEvent(SEARCH_EVENTS.AutocompleteSuggestionClick, {
                searchText: input.data.searchText,
                suggestionText: input.data.suggestionText,
            });
        case 'resultClick':
            return createGTMEcommerceEvent(SEARCH_EVENTS.SearchResultClick, {
                searchText: input.data.searchText,
                clickName: input.data.clickedName,
            });
        default:
            throw new Error('Unsupported search source');
    }
};

const PRODUCT_CLICK_EVENTS = {
    autocomplete: SEARCH_EVENTS.AutocompleteProductClick,
    recommendations: SEARCH_EVENTS.ProductRecommendationClick,
    searchResults: SEARCH_EVENTS.SearchProductClick,
} as const satisfies Record<SearchProductClickInput['source'], GTMEventName>;

const PRODUCT_CLICK_LIST_NAMES = {
    autocomplete: 'search-autocomplete',
    recommendations: 'search-product',
    searchResults: 'search-product',
} as const satisfies Record<SearchProductClickInput['source'], string>;

export const createSearchProductClickGAEvent = (input: SearchProductClickInput) => {
    const product = input.data.product;
    const itemListName = PRODUCT_CLICK_LIST_NAMES[input.source];

    return createGTMEvent(PRODUCT_CLICK_EVENTS[input.source], {
        item_id: getProductId(product),
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.category,
        item_variant: mapProductVariant(product),
        currency: DEFAULT_CURRENCY,
        item_price: product.fullPrice,
        item_list_name: itemListName,
        search_term: input.source === 'recommendations' ? null : input.data.searchText,
        user_id: getBPId() || 'Guest',
    });
};
